import React from 'react';
import { graphql } from 'gatsby';
import Layout from '../../components/Layout/Layout';
import HeroPage from '../../components/Global/HeroPage/HeroPage';
import ImageWrapper from '../../components/Global/Image/ImageWrapper';
import StructuredContentDefault from '../../components/StructuredContentDefault ';
import AuthorCard from '../../components/Global/AuthorCard/AuthorCard';
import { isArray } from '../../utils';
import SeoDatoCms from '../../components/SeoDatoCms';
import LatestUpdates from '../../components/Blocks/LatestUpdates/LatestUpdates';
import HubspotForm from '../../components/Blocks/HubspotForm/HubspotForm';
import SharePage from '../../components/Global/SharePage/SharePage';
import FadeIn from '../../components/Global/FadeIn/FadeIn';

import './index.scss';

const Post = ({ pageContext, location, data: { page, pressReleaseForm, breadcrumb, favicon, siteTitle } }) => {
  const {
    seo,
    title,
    introduction,
    date,
    image,
    includeImageInPostDetail = true,
    authors = [],
    textContent,
    oldNid,
    noIndex,
  } = page;

  const isPressRelease = page.tags.some((tag) => tag.slug === 'press-releases');
  const { showPressReleaseForm, form, pressContactName, pressContactEmail, pressContactPicture } = pressReleaseForm;

  return (
    <Layout>
      <SeoDatoCms seo={seo} favicon={favicon} siteTitle={siteTitle}>
        {noIndex && <meta name="robots" content="noindex" />}
      </SeoDatoCms>

      <HeroPage
        title={title}
        context={pageContext}
        date={date}
        location={location}
        breadcrumb={breadcrumb}
        breadcrumbDetail={title}
        isDetailView
        alignHeader
      />

      <div id="main-content" className="container">
        <FadeIn>
          <div className="post-detail">
            <div className="row justify-content-center">
              <div className="col-lg-9">
                {introduction && (
                  <div className="introduction-field">
                    <div dangerouslySetInnerHTML={{ __html: introduction }} />
                  </div>
                )}

                {includeImageInPostDetail && image && (
                  <div className="post-main-image">
                    <ImageWrapper image={image} />
                  </div>
                )}

                {isArray(authors) && (
                  <div className="authors-list row gy-4">
                    {authors.map((author) => (
                      <div className="col-lg-6" key={author.id}>
                        <AuthorCard author={author} />
                      </div>
                    ))}
                  </div>
                )}

                {textContent && (
                  <div className={`post-text-content link-and-list-styles-v2 ${oldNid ? 'importer-post' : ''}`}>
                    <StructuredContentDefault content={textContent} />
                  </div>
                )}

                {/* Form block */}
                {isPressRelease && showPressReleaseForm && form && (
                  <>
                    <div className="press-release-form-wrapper">
                      {pressReleaseForm.title && <h2>{pressReleaseForm.title}</h2>}
                      <HubspotForm {...pressReleaseForm.form} />
                    </div>
                    <div className="contact-info">
                      <h3>Press Contact</h3>
                      <div className="metadata">
                        <div className="image">
                          <img src={pressContactPicture.url} />
                        </div>
                        <div>
                          <h4>{pressContactName}</h4>
                          <a className="email" href={`mailto:${pressContactEmail}`}>
                            {pressContactEmail}
                          </a>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <LatestUpdates
            block={{
              title: 'Related News',
              link: { label: 'See all updates', content: { slug: 'news', model: { apiKey: 'list_news' } } },
            }}
            variant="grey"
          />
        </FadeIn>
      </div>
      <SharePage />
    </Layout>
  );
};

export const PostQuery = graphql`
  query PostById($id: String, $menuPos: String) {
    favicon: datoCmsSite {
      faviconMetaTags {
        ...GatsbyDatoCmsFaviconMetaTags
      }
    }
    siteTitle: datoCmsSite {
      globalSeo {
        siteName
      }
    }
    breadcrumb: datoCmsPageStructure(id: { eq: $menuPos }) {
      ...Breadcrumb
    }
    pressReleaseForm: datoCmsGlobalConfig {
      title
      showPressReleaseForm
      pressContactName
      pressContactEmail
      pressContactPicture {
        url
        alt
      }
      form {
        ...Hubspot
      }
    }
    page: datoCmsPost(id: { eq: $id }) {
      title
      noIndex
      introduction
      image {
        gatsbyImageData(width: 985, height: 656)
        alt
        title
      }
      includeImageInPostDetail
      date(formatString: "D MMM Y")
      textContent {
        value
        blocks {
          __typename
          ... on DatoCmsAcordion {
            id: originalId
            items {
              title
              text
            }
          }
          ... on DatoCmsEmbedVideo {
            id: originalId
            video {
              url
              providerUid
            }
          }
          ... on DatoCmsEmbedIframe {
            id: originalId
            embedCode
          }
          ... on DatoCmsImage {
            id: originalId
            image {
              gatsbyImageData
              alt
              title
            }
          }
        }
      }
      tags {
        ... on DatoCmsTagNews {
          id
          slug
          title
          color
        }
      }
      authors {
        id
        name
        slug
        description
        jobPosition
        image {
          alt
          gatsbyImageData(width: 84, height: 84)
        }
      }
      form {
        ...BlockTextHubspot
      }
      oldNid
      seo: seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }
  }
`;

export default Post;
